<template>
    <Transition name="banner-slide">
        <div v-if="games?.length" class="game-day-banner-wrapper">
            <v-sheet class="game-day-banner">
            <!-- ◀️ Starší -->
            <div class="nav-btn" @click="fetchPrevPage">
                <v-icon>mdi-chevron-left</v-icon>
            </div>


            <!-- 🏒 Scrolovací ticker -->
            <div class="games-scroll" ref="scrollContainer">
                <template v-for="(group, gIndex) in groupedGames" :key="gIndex">
                    <!-- 📅 Datum -->
                    <div class="date-col">
                        <div class="date-label">
                            <span class="date-day">{{ formatDay(group.date) }}</span>
                            <span class="date-month">{{ formatMonth(group.date) }}</span>
                        </div>
                    </div>

                    <!-- Zápasy dne -->
                    <div v-for="game in group.games" :key="game._id" class="game-card" :data-idx="game.__idx"
                        :class="{ 'is-highlighted': game._id === highlightedGameId }"
                        @click="router.push({ path: `/gamecenter/${game._id}` })">

                        <!-- Goal overlay -->
                        <Transition name="gdb-goal">
                          <div
                            v-if="goalOverlayGameId === game._id"
                            class="game-card__goal-overlay"
                            :style="{ '--goal-team-color': goalOverlayColor }"
                          >
                            <img
                              v-if="goalOverlayLogo"
                              :src="getTeamImage(goalOverlayLogo)"
                              class="game-card__goal-logo"
                              alt=""
                            />
                            <span class="game-card__goal-text">{{ $t('goalOverlay') }}</span>
                          </div>
                        </Transition>

                        <div class="time-label">
                            <div v-if="game?.gameStatus?.lastRecordTime">
                                <span v-if="!game?.gameStatus?.matchCompleted" class="live-dot"></span>
                                {{ gameStatusLabel(game) }}
                            </div>
                            <div v-else>
                                {{ game.timeFrom }}
                            </div>
                        </div>

                        <div class="teams">
                            <div class="team-row">
                                <img :src="getTeamImage(game.meta.homeTeam.logo)" class="team-logo" />
                                <span class="team-code">{{ game.meta.homeTeamCode }}</span>
                                <span v-if="game?.gameStatus?.lastRecordTime" class="team-record"
                                    :class="{ 'team-record--losing': isHomeLosing(game) }">
                                    {{ game.gameStatus?.home?.goals ?? game.meta.homeScore }}
                                </span>
                                <span v-else class="team-stats">{{ game.meta.homeStats }}</span>
                            </div>

                            <div class="team-row">
                                <img :src="getTeamImage(game.meta.awayTeam.logo)" class="team-logo" />
                                <span class="team-code">{{ game.meta.awayTeamCode }}</span>
                                <span v-if="game?.gameStatus?.lastRecordTime" class="team-record"
                                    :class="{ 'team-record--losing': isAwayLosing(game) }">
                                    {{ game.gameStatus?.away?.goals ?? game.meta.awayScore }}
                                </span>
                                <span v-else class="team-stats">{{ game.meta.awayStats }}</span>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <!-- ▶️ Novější -->
            <div class="nav-btn" @click="fetchNextPage">
                <v-icon>mdi-chevron-right</v-icon>
            </div>
        </v-sheet>
    </div>
    </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watchEffect, nextTick, watch } from 'vue'
import { format, parseISO } from 'date-fns'
import { cs } from 'date-fns/locale'
import axiosInstance from '@/services/axiosInstance'
import { getTeamImage } from '@/utils/imageUtils'
import { getShotcut } from '@/utils/teamUtils'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app/appStore'
import { useUserStore } from '@/stores/user/userStore'
import { io, Socket } from 'socket.io-client'

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000'

const userStore = useUserStore() // ⬅️ vytvoř instanci userStore

const router = useRouter()
const appStore = useAppStore()
const { t } = useI18n()

/** OT/SN aware status label */
function gameStatusLabel(game: GameVM): string {
    const gs = game.gameStatus
    if (!gs) return ''
    const isCompleted = !!gs.matchCompleted
    const isOT = !!gs.overtime
    const isSN = !!gs.shootouts
    if (isCompleted) {
        if (isSN) return t('finalSN')
        if (isOT) return t('finalOT')
        return t('final')
    }
    if (isSN) return t('liveSN')
    if (isOT) return t('liveOT')
    return t('live')
}

/** ===== Typy ===== */
type TickerEvent = {
    id: string
    dateFrom: string         // "YYYY-MM-DD" (den)
    dateFromRaw: string      // ISO datetime hranice pro stránkování
    time: string
    homeTeam: any
    awayTeam: any
    league?: string
    pinned?: boolean
    gameStatus?: any
}

type GameVM = {
    _id: string
    date: string
    dateRaw: string
    timeFrom: string
    gameStatus?: any
    meta: {
        homeTeam: any
        awayTeam: any
        homeTeamCode: string
        awayTeamCode: string
        homeScore?: number | null
        awayScore?: number | null
        homeStats?: string
        awayStats?: string
    }
    __idx?: number
}

/** ===== Stav ===== */
const games = ref<GameVM[]>([])
const LIMIT = 10

/** ===== Highlighted game: live or nearest upcoming ===== */
const highlightedGameId = computed(() => {
    const now = Date.now()
    const sorted = flatGames()
    if (!sorted.length) return null

    // 1) prefer a currently live game (has lastRecordTime and not completed)
    const live = sorted.find(g =>
        g.gameStatus?.lastRecordTime && !g.gameStatus?.matchCompleted
    )
    if (live) return live._id

    // 2) nearest future game (dateRaw >= now)
    const upcoming = sorted.find(g => {
        const t = new Date(g.dateRaw).getTime()
        return t >= now
    })
    if (upcoming) return upcoming._id

    // 3) fallback: last game in list (most recent past)
    return sorted[sorted.length - 1]._id
})

/** ===== Formátování dne ===== */
const formatDate = (dateStr: string) => {
    const str = format(parseISO(dateStr), 'd MMM', { locale: cs })
    return str.toUpperCase()
}

/** ===== Grouping + indexování pro IO ===== */
const groupedGames = computed(() => {
    const map = new Map<string, GameVM[]>()
    const sorted = games.value.slice().sort((a, b) => {
        if (a.date === b.date) return a.timeFrom.localeCompare(b.timeFrom)
        return a.date.localeCompare(b.date)
    })

    let idx = 0
    for (const g of sorted) {
        g.__idx = idx++
        if (!map.has(g.date)) map.set(g.date, [])
        map.get(g.date)!.push(g)
    }
    return Array.from(map.entries()).map(([date, games]) => ({ date, games }))
})

/** ===== FETCH – prvotní načtení: NOW - 5h ===== */
async function fetchInitial() {
    try {
        const fiveHoursAgoIso = new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString()
        const { data } = await axiosInstance.get<TickerEvent[]>('/events/event-ticker', {
            params: { limit: LIMIT, from: fiveHoursAgoIso },
        })
        mapAndSet(data)
    } catch (err) {
        console.error('Chyba při načítání ticker eventů:', err)
        games.value = []
        appStore.setTickerEventsCount(0)
        await nextTick()
    }
}

/** ===== NEXT/PREV – stránkování ===== */
function getLastVisibleGame(): GameVM | null {
    const all = flatGames()
    return all[all.length - 1] || null
}

function getFirstGame(): GameVM | null {
    const all = flatGames()
    return all[0] || null
}

function flatGames(): GameVM[] {
    return games.value.slice().sort((a, b) => {
        if (a.date === b.date) return a.timeFrom.localeCompare(b.timeFrom)
        return a.date.localeCompare(b.date)
    })
}

async function fetchNextPage() {
    const last = getLastVisibleGame()
    if (!last?.dateRaw) return
    await fetchPage({ from: last.dateRaw })
}

async function fetchPrevPage() {
    const first = getFirstGame()
    if (!first?.dateRaw) return
    await fetchPage({ to: first.dateRaw })
}

async function fetchPage(params: Record<string, any>) {
    try {
        const { data } = await axiosInstance.get<TickerEvent[]>('/events/event-ticker', {
            params: { limit: LIMIT, ...params },
        })
        mapAndSet(data)
    } catch (err) {
        console.error('Chyba při stránkování tickeru:', err)
    }
}



function getScores(game: GameVM) {
    const home = game.gameStatus?.home?.goals ?? game.meta.homeScore
    const away = game.gameStatus?.away?.goals ?? game.meta.awayScore
    return { home, away }
}

function isHomeLosing(game: GameVM) {
    const { home, away } = getScores(game)
    if (home == null || away == null) return false
    return home < away
}

function isAwayLosing(game: GameVM) {
    const { home, away } = getScores(game)
    if (home == null || away == null) return false
    return away < home
}

/** ===== Mapování dat + IO reattach + scroll reset ===== */
async function mapAndSet(data: TickerEvent[]) {
    games.value = (data || []).map((ev) => ({
        _id: ev.id,
        date: ev.dateFrom,
        dateRaw: ev.dateFromRaw,
        timeFrom: ev.time || '',
        gameStatus: ev.gameStatus || {},
        meta: {
            homeTeam: ev.homeTeam,
            awayTeam: ev.awayTeam,
            homeTeamCode: getShotcut(ev.homeTeam?.name),
            awayTeamCode: getShotcut(ev.awayTeam?.name),
            homeScore: ev.homeTeam?.score ?? null,
            awayScore: ev.awayTeam?.score ?? null,
            homeStats: `${ev.homeTeam?.seasonStats?.totalWins || 0}-${ev.homeTeam?.seasonStats?.totalLosses || 0}-${ev.homeTeam?.seasonStats?.overTimeWin + ev.homeTeam?.seasonStats?.overTimeLoss || 0}`,
            awayStats: `${ev.awayTeam?.seasonStats?.totalWins || 0}-${ev.awayTeam?.seasonStats?.totalLosses || 0}-${ev.awayTeam?.seasonStats?.overTimeWin + ev.awayTeam?.seasonStats?.overTimeLoss || 0}`
        },
    }))

    appStore.setTickerEventsCount(games.value.length)

    await nextTick()

    // 👇 po načtení naskroluj na highlighted kartu
    scrollToHighlighted()

    // 👇 po načtení se připoj k socket rooms pro všechny hry
    joinAllGameRooms()
}

/** ===== Socket.IO – live updates ===== */
const socket: Socket = io(SOCKET_URL)
const joinedRooms = ref<Set<string>>(new Set())

// Goal overlay state
const goalOverlayGameId = ref<string | null>(null)
const goalOverlayLogo = ref('')
const goalOverlayColor = ref('#00D4FF')
let goalOverlayTimer: ReturnType<typeof setTimeout> | null = null

function joinAllGameRooms() {
    // Leave old rooms
    for (const roomId of joinedRooms.value) {
        socket.emit('leaveEvent', roomId)
    }
    joinedRooms.value.clear()

    // Join rooms for all loaded games
    if (socket.disconnected) socket.connect()
    for (const game of games.value) {
        socket.emit('joinEvent', game._id)
        joinedRooms.value.add(game._id)
    }
}

function findGameByEventId(eventId: string): GameVM | undefined {
    // The socket message is emitted to room = eventId.
    // We need to match against _id of our games.
    return games.value.find(g => g._id === eventId)
}

// Socket handlers — we need to determine which game an event belongs to.
// Since socket.io-client receives events for all rooms, we identify the game
// by checking the event data structure.

function onTickerEventUpdate(data: any) {
    // eventUpdate comes with full/partial event data. It includes _id.
    const eventId = data._id || data.id
    if (!eventId) return
    const game = findGameByEventId(eventId)
    if (!game) return

    // Update gameStatus if present
    if (data.gameStatus) {
        game.gameStatus = { ...game.gameStatus, ...data.gameStatus }
    }
    // Update meta teams if present
    if (data.meta?.homeTeam) {
        Object.assign(game.meta.homeTeam, data.meta.homeTeam)
    }
    if (data.meta?.awayTeam) {
        Object.assign(game.meta.awayTeam, data.meta.awayTeam)
    }
}

function onTickerPlayByPlay(record: any) {
    // PBP records don't contain eventId directly — they are emitted to a room.
    // Since we're in multiple rooms, we need to identify the game by the teams.
    // The record has record.team._id which we can match to home/away team IDs.
    if (record.type === 'goal') {
        // Find which game this belongs to using team IDs
        const teamId = record.team?._id
        if (!teamId) return
        const game = games.value.find(g => {
            const homeId = g.meta.homeTeam?._id || g.meta.homeTeam?.id
            const awayId = g.meta.awayTeam?._id || g.meta.awayTeam?.id
            return homeId === teamId || awayId === teamId
        })
        if (game) {
            triggerGoalOverlay(game, record)
        }
    }
}

function onTickerGameStatus(data: any) {
    // 1) Match by _eventId (most reliable — server always includes it)
    let game: GameVM | undefined
    if (data._eventId) {
        game = games.value.find(g => g._id === data._eventId)
    }

    // 2) Fallback: match by team IDs (for backward compat with old updateGameStatus calls)
    if (!game) {
        const homeId = data.home?._id || data.home?.id
        const awayId = data.away?._id || data.away?.id
        if (!homeId && !awayId) return

        game = games.value.find(g => {
            const gHomeId = g.meta.homeTeam?._id || g.meta.homeTeam?.id
            const gAwayId = g.meta.awayTeam?._id || g.meta.awayTeam?.id
            return (homeId && gHomeId === homeId) || (awayId && gAwayId === awayId)
        })
    }
    if (!game) return

    game.gameStatus = { ...game.gameStatus, ...data }
}

function triggerGoalOverlay(game: GameVM, record: any) {
    const teamId = record.team?._id
    const homeId = game.meta.homeTeam?._id || game.meta.homeTeam?.id

    if (teamId === homeId) {
        goalOverlayLogo.value = game.meta.homeTeam?.logo || game.meta.homeTeam?.imageUrl || ''
        goalOverlayColor.value = game.meta.homeTeam?.primaryTeamColor || '#00D4FF'
    } else {
        goalOverlayLogo.value = game.meta.awayTeam?.logo || game.meta.awayTeam?.imageUrl || ''
        goalOverlayColor.value = game.meta.awayTeam?.primaryTeamColor || '#00D4FF'
    }

    if (goalOverlayTimer) clearTimeout(goalOverlayTimer)
    goalOverlayGameId.value = game._id
    goalOverlayTimer = setTimeout(() => {
        goalOverlayGameId.value = null
    }, 3000)

    // Scroll to the game card with goal
    nextTick(() => {
        const root = scrollContainer.value
        if (!root) return
        const cards = Array.from(root.querySelectorAll('.game-card')) as HTMLElement[]
        for (const card of cards) {
            const idx = card.dataset.idx
            if (idx == null) continue
            const sorted = flatGames()
            const g = sorted.find(g2 => g2.__idx === Number(idx))
            if (g && g._id === game._id) {
                card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
                break
            }
        }
    })
}

function attachTickerSocketHandlers() {
    socket.on('eventUpdate', onTickerEventUpdate)
    socket.on('playByPlay', onTickerPlayByPlay)
    socket.on('updateGameStatus', onTickerGameStatus)
}

function detachTickerSocketHandlers() {
    socket.off('eventUpdate', onTickerEventUpdate)
    socket.off('playByPlay', onTickerPlayByPlay)
    socket.off('updateGameStatus', onTickerGameStatus)
}

const scrollContainer = ref<HTMLElement | null>(null)

/** ===== Scroll to highlighted card ===== */
function scrollToHighlighted() {
    const root = scrollContainer.value
    const id = highlightedGameId.value
    if (!root || !id) {
        if (root) root.scrollTo({ left: 0, behavior: 'auto' })
        return
    }
    const cards = Array.from(root.querySelectorAll('.game-card')) as HTMLElement[]
    // find the card and its preceding date-col
    for (const card of cards) {
        // match by _id via the rendered order
        const idx = card.dataset.idx
        if (idx == null) continue
        const sorted = flatGames()
        const game = sorted.find(g => g.__idx === Number(idx))
        if (game && game._id === id) {
            // scroll so the date-col before this card group is visible
            const prev = card.previousElementSibling as HTMLElement | null
            const target = prev?.classList.contains('date-col') ? prev : card
            target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
            return
        }
    }
}

onMounted(async () => {
    attachTickerSocketHandlers()
    await fetchInitial()
    nextTick(() => setupAutoSnap())
})

onUnmounted(() => {
    // Leave all rooms
    for (const roomId of joinedRooms.value) {
        socket.emit('leaveEvent', roomId)
    }
    joinedRooms.value.clear()
    detachTickerSocketHandlers()
    socket.disconnect()
    if (goalOverlayTimer) clearTimeout(goalOverlayTimer)
})



/** ===== Automatické dorovnání po ručním scrollu ===== */
function setupAutoSnap() {
    const root = scrollContainer.value
    if (!root) return

    let timeout: any = null

    root.addEventListener('scroll', () => {
        clearTimeout(timeout)
        // počkej, až uživatel přestane scrollovat
        timeout = setTimeout(() => snapToNearest(root), 150)
    })
}

function snapToNearest(container: HTMLElement) {
    const children = Array.from(container.querySelectorAll('.date-col, .game-card')) as HTMLElement[]
    if (!children.length) return

    const containerLeft = container.getBoundingClientRect().left

    // najdi prvek nejblíže levému okraji
    let nearest: HTMLElement | null = null
    let minDistance = Infinity

    for (const el of children) {
        const rect = el.getBoundingClientRect()
        const distance = Math.abs(rect.left - containerLeft)
        if (distance < minDistance) {
            minDistance = distance
            nearest = el
        }
    }

    if (nearest) {
        nearest.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    }
}


/** ===== Sleduj změnu přihlášeného uživatele ===== */
watch(
    () => userStore.user,
    async (newUser) => {
        if (!newUser) {
            // 🧹 odhlášený → vymaž data
            games.value = []
            //appStore.setTickerEventsCount(0)
            await fetchInitial()
        } else {
            // 👤 přihlášený → znovu načti data
            await fetchInitial()
            await nextTick()
            setupAutoSnap()
        }
    },
    { immediate: true } // při mountu spustí jednou automaticky
)


watchEffect(async () => {
    if (appStore.needRefetch) {
        await fetchInitial()
        await nextTick()
        setupAutoSnap()
        appStore.clearRefetchFlag()
    }
})


const formatDay = (dateStr: string) =>
    format(parseISO(dateStr), 'd', { locale: cs })

// KVĚ (z "kvě")
const formatMonth = (dateStr: string) =>
    format(parseISO(dateStr), 'LLL', { locale: cs }).toUpperCase()


</script>

<style scoped lang="scss">
// ── Design tokens – dark (default) ─────────────────────────────────────────
// Tokens are exposed as CSS custom properties so the light theme can override.
.game-day-banner-wrapper {
    --gdb-bg:          rgba(6, 8, 16, 0.97);
    --gdb-t1:          #FFFFFF;
    --gdb-t2:          rgba(255, 255, 255, 0.65);
    --gdb-t3:          rgba(255, 255, 255, 0.35);
    --gdb-b1:          rgba(255, 255, 255, 0.08);
    --gdb-overlay:     rgba(255, 255, 255, 0.04);
    --gdb-ice:         #00D4FF;
    --gdb-ice-soft:    rgba(0, 212, 255, 0.12);
}

// ── Light-theme overrides ──────────────────────────────────────────────────
:global(.v-theme--light) .game-day-banner-wrapper {
    --gdb-bg:          rgba(255, 255, 255, 0.97);
    --gdb-t1:          #1a1a1a;
    --gdb-t2:          rgba(0, 0, 0, 0.6);
    --gdb-t3:          rgba(0, 0, 0, 0.38);
    --gdb-b1:          rgba(0, 0, 0, 0.08);
    --gdb-overlay:     rgba(0, 0, 0, 0.04);
    --gdb-ice:         #00D4FF;
    --gdb-ice-soft:    rgba(0, 212, 255, 0.12);
}

// ── Wrapper ────────────────────────────────────────────────────────────────
.game-day-banner-wrapper {
    width: 100%;
    height: 76px;
    position: relative;
    z-index: 100;
    flex-shrink: 0;
}

// ── Main bar ───────────────────────────────────────────────────────────────
.game-day-banner {
    display: flex;
    align-items: stretch;
    width: 100%;
    overflow: hidden;
    background: var(--gdb-bg) !important;
    backdrop-filter: blur(20px);
    position: relative;
    z-index: 10;
    height: 100%;
    border-bottom: 1px solid var(--gdb-b1);
    color: var(--gdb-t1) !important;
    border-radius: 0 !important;
}

// ── Nav buttons ────────────────────────────────────────────────────────────
.nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    flex-shrink: 0;
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
    border-right: 1px solid var(--gdb-b1);
    color: var(--gdb-t3) !important;

    &:last-child {
        border-right: none;
        border-left: 1px solid var(--gdb-b1);
    }

    :deep(.v-icon) {
        color: inherit !important;
        font-size: 20px;
    }

    &:hover {
        color: var(--gdb-ice) !important;
    }
}

// ── Date column ────────────────────────────────────────────────────────────
.date-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    border-right: 1px solid var(--gdb-b1);
    flex-shrink: 0;
    scroll-snap-align: start;
}

.date-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.05;
}

.date-day {
    font-weight: 900;
    font-size: 22px;
    line-height: 1;
    color: var(--gdb-t1);
}

.date-month {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--gdb-t3);
    margin-top: 2px;
}

// ── Scroll container ───────────────────────────────────────────────────────
.games-scroll {
    flex: 1;
    display: flex;
    align-items: stretch;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
        display: none;
    }
}

// ── Individual game card ───────────────────────────────────────────────────
.game-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 170px;
    padding: 10px 18px;
    border-right: 1px solid var(--gdb-b1);
    text-align: left;
    transition: background 0.2s;
    cursor: pointer;
    background: transparent !important;
    color: var(--gdb-t1) !important;
    scroll-snap-align: start;

    &:hover {
        background: var(--gdb-overlay) !important;
    }

    &.is-highlighted {
        background: var(--gdb-ice-soft) !important;
        border-bottom: 2px solid var(--gdb-ice);
    }
}

// ── Time / status label ────────────────────────────────────────────────────
.time-label {
    font-size: 10px;
    font-weight: 700;
    color: var(--gdb-ice) !important;
    letter-spacing: 0.5px;
    margin-bottom: 5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 4px;
    background: none !important;
    padding: 0;
    border-radius: 0;
}

// ── Team rows ──────────────────────────────────────────────────────────────
.teams {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.team-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    color: var(--gdb-t1) !important;

    .team-logo {
        width: 16px;
        height: 16px;
        object-fit: contain;
        border-radius: 3px;
        flex-shrink: 0;
    }

    .team-code {
        font-weight: 700;
        font-size: 13px;
        flex: 1;
        color: var(--gdb-t1) !important;
        letter-spacing: 0.3px;
    }

    .team-record {
        font-size: 13px;
        font-weight: 800;
        color: var(--gdb-t1) !important;
    }

    .team-record--losing {
        color: var(--gdb-t3) !important;
    }

    .team-stats {
        font-size: 10px;
        color: var(--gdb-t3) !important;
    }
}

// ── Live pulse dot ─────────────────────────────────────────────────────────
.live-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    background-color: #22C55E;
    border-radius: 50%;
    box-shadow: 0 0 0 rgba(34, 197, 94, 0.4);
    animation: pulse 1.5s infinite;
    vertical-align: middle;
    flex-shrink: 0;
}

@keyframes pulse {
    0%   { transform: scale(1);   box-shadow: 0 0 0 0   rgba(34, 197, 94, 0.4); }
    70%  { transform: scale(1.3); box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);   }
    100% { transform: scale(1);   box-shadow: 0 0 0 0   rgba(34, 197, 94, 0);   }
}

// ── Banner slide animation ─────────────────────────────────────────────────
.banner-slide-enter-active,
.banner-slide-leave-active {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
}

.banner-slide-enter-from,
.banner-slide-leave-to {
    max-height: 0 !important;
    opacity: 0;
}

.banner-slide-enter-to,
.banner-slide-leave-from {
    max-height: 76px;
    opacity: 1;
}

// ── Game card goal overlay ─────────────────────────────────────────────────
.game-card {
    position: relative;
    overflow: hidden;
}

.game-card__goal-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.92);
    pointer-events: none;
    animation: gdbGoalFlash 3s ease-out forwards;
}

.game-card__goal-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    filter: drop-shadow(0 0 10px var(--goal-team-color, #00D4FF));
    animation: gdbLogoGlow 1.2s ease-in-out infinite alternate;
}

.game-card__goal-text {
    font-size: 16px;
    font-weight: 900;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #fff;
    text-shadow:
        0 0 10px var(--goal-team-color, #00D4FF),
        0 0 25px var(--goal-team-color, #00D4FF);
    animation: gdbTextPulse 0.8s ease-in-out infinite alternate;
}

// ── Goal overlay transition ──
.gdb-goal-enter-active {
    animation: gdbGoalIn 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.gdb-goal-leave-active {
    animation: gdbGoalOut 0.4s cubic-bezier(0.55, 0, 1, 0.45) forwards;
}

@keyframes gdbGoalIn {
    0%   { opacity: 0; transform: scale(1.15); }
    100% { opacity: 1; transform: scale(1); }
}

@keyframes gdbGoalOut {
    0%   { opacity: 1; }
    100% { opacity: 0; }
}

@keyframes gdbGoalFlash {
    0%   { border-color: var(--goal-team-color, #00D4FF); box-shadow: inset 0 0 20px rgba(255,255,255,0.15); }
    20%  { box-shadow: inset 0 0 30px rgba(255,255,255,0.08); }
    100% { box-shadow: inset 0 0 0 rgba(255,255,255,0); }
}

@keyframes gdbLogoGlow {
    0%   { filter: drop-shadow(0 0 8px var(--goal-team-color, #00D4FF)); }
    100% { filter: drop-shadow(0 0 18px var(--goal-team-color, #00D4FF)) brightness(1.15); }
}

@keyframes gdbTextPulse {
    0%   { letter-spacing: 3px; }
    100% { letter-spacing: 5px; }
}
</style>
